import { useEffect, useMemo, useRef, useState } from "react";
import "./EditarDesvioEditarModal.css";

const toUpperSafe = (v) => (typeof v === "string" ? v.toUpperCase() : v ?? "");
const ATIVO_OPTIONS = [
  { value: 1, label: "SIM" },
  { value: 0, label: "NAO" },
];

function EditarDesvioEditarDropdown({ label, name, value, options, onSelect, placeholder = "SELECIONE" }) {
  const [open, setOpen] = useState(false);
  const [renderMenu, setRenderMenu] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const [menuMaxHeight, setMenuMaxHeight] = useState(220);
  const wrapRef = useRef(null);
  const closeTimerRef = useRef(null);
  const itemHeightRef = 44;

  const current = useMemo(
    () => options.find((o) => String(o.value) === String(value)) || null,
    [options, value]
  );

  useEffect(() => {
    function onDocDown(e) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) closeMenu();
    }
    document.addEventListener("mousedown", onDocDown);
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const onWindowChange = () => updatePlacement();
    window.addEventListener("resize", onWindowChange);
    window.addEventListener("scroll", onWindowChange, true);
    return () => {
      window.removeEventListener("resize", onWindowChange);
      window.removeEventListener("scroll", onWindowChange, true);
    };
  }, [open, options.length]);

  function updatePlacement() {
    if (!wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const modal = wrapRef.current.closest(".dveem-modal");
    const modalRect = modal?.getBoundingClientRect();

    const viewportH = window.innerHeight || document.documentElement.clientHeight;
    const safePad = 12;

    const viewportBelow = Math.max(0, viewportH - rect.bottom - safePad);
    const viewportAbove = Math.max(0, rect.top - safePad);
    const modalBelow = modalRect ? Math.max(0, modalRect.bottom - rect.bottom - safePad) : Number.POSITIVE_INFINITY;
    const modalAbove = modalRect ? Math.max(0, rect.top - modalRect.top - safePad) : Number.POSITIVE_INFINITY;

    const spaceBelow = Math.max(0, Math.min(viewportBelow, modalBelow));
    const spaceAbove = Math.max(0, Math.min(viewportAbove, modalAbove));

    const target = Math.min(320, options.length * itemHeightRef + 12);
    const shouldOpenUp = spaceBelow < 190 && spaceAbove > spaceBelow;
    const available = shouldOpenUp ? spaceAbove : spaceBelow;
    const dynamicMax = Math.max(96, Math.min(target, available - 6));

    setOpenUp(shouldOpenUp);
    setMenuMaxHeight(dynamicMax);
  }

  const openMenu = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setRenderMenu(true);
    requestAnimationFrame(() => {
      updatePlacement();
      setOpen(true);
    });
  };

  const closeMenu = () => {
    setOpen(false);
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setRenderMenu(false), 240);
  };

  const toggle = () => {
    if (open) closeMenu();
    else openMenu();
  };

  return (
    <div ref={wrapRef} className={`dveem-field dveem-field-select ${open ? "is-open" : ""} ${openUp ? "is-open-up" : ""}`}>
      <span className="dveem-label">{label}</span>
      <div className="dveem-pick-anchor">
        <button
          type="button"
          className="dveem-pick-btn"
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={toggle}
        >
          <span className={`dveem-pick-val ${current ? "" : "is-placeholder"}`}>
            {current ? current.label : placeholder}
          </span>
        </button>

        {renderMenu && (
          <div
            className={`dveem-pick-menu ${open ? "is-open" : "is-closing"}`}
            role="listbox"
            aria-label={label}
            style={{ maxHeight: `${menuMaxHeight}px` }}
          >
            {options.map((opt) => (
              <button
                key={String(opt.value)}
                type="button"
                className="dveem-pick-opt"
                onClick={() => {
                  onSelect(name, opt.value);
                  closeMenu();
                }}
                role="option"
                aria-selected={String(opt.value) === String(value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function EditarDesvioEditarModal({ open, initial, onClose, onSubmit }) {
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (!open) return;
    setForm({
      id: initial?.id ?? null,
      codigo: initial?.codigo ?? "",
      causador: initial?.causador ?? "",
      descricao: initial?.descricao ?? "",
      ordem: Number.isFinite(Number(initial?.ordem)) ? Number(initial.ordem) : 0,
      ativo: Number(initial?.ativo ?? 1) ? 1 : 0,
    });
  }, [open, initial]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "Enter" && document.activeElement?.tagName?.toLowerCase() !== "textarea") {
        e.preventDefault();
        handleSubmit();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, form]);

  if (!open || !form) return null;

  const canSave = !!String(form.causador || "").trim();

  function handleSubmit() {
    if (!canSave) return;
    onSubmit?.({
      id: form.id,
      codigo: form.codigo,
      causador: form.causador,
      descricao: form.descricao,
      ordem: form.ordem,
      ativo: form.ativo,
    });
  }

  return (
    <div className="dveem-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
      <div className="dveem-modal" role="dialog" aria-modal="true" aria-label="Editar Desvio Editar">
        <header className="dveem-header">
          <h3 className="dveem-title">Editar</h3>
          <button type="button" className="dveem-close" aria-label="Fechar" onClick={onClose}>
            x
          </button>
        </header>

        <div className="dveem-body">
          <div className="dveem-grid">
            <label className="dveem-field">
              <span className="dveem-label">Causador *</span>
              <input
                value={form.causador}
                onChange={(e) => setForm((prev) => ({ ...prev, causador: toUpperSafe(e.target.value) }))}
                placeholder="Ex.: SISTEMA"
              />
            </label>

            <EditarDesvioEditarDropdown
              label="Ativo"
              name="ativo"
              value={form.ativo}
              options={ATIVO_OPTIONS}
              onSelect={(name, nextValue) => setForm((prev) => ({ ...prev, [name]: nextValue }))}
              placeholder="SELECIONE"
            />

            <label className="dveem-field dveem-field--full">
              <span className="dveem-label">Descricao</span>
              <textarea
                rows={4}
                value={form.descricao}
                onChange={(e) => setForm((prev) => ({ ...prev, descricao: e.target.value }))}
                placeholder="Detalhe da justificativa..."
              />
            </label>
          </div>
        </div>

        <footer className="dveem-footer">
          <button type="button" className="dveem-btn dveem-btn--ghost" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="dveem-btn dveem-btn--primary" onClick={handleSubmit} disabled={!canSave}>
            Salvar
          </button>
        </footer>
      </div>
    </div>
  );
}
